import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ProximityToggle } from "@/components/ProximityToggle";
import { SocialConsent, SOCIAL_MSG, MessagesConsent } from "@/components/SocialConsent";
import { RulesBox, RULES_MSG } from "@/components/RulesBox";
import api, { errMsg } from "@/lib/api";
import { SUBCATEGORIES } from "@/lib/categories";
import { COUNTRIES, CITIES_BY_COUNTRY, cityCoords, nearCountries } from "@/lib/places";
import { Input } from "@/components/ui/input";
import { CityInput, AddressInput } from "@/components/AddressFields";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function GenericForm({ category, onDone, initial }) {
  const nav = useNavigate();
  const [f, setF] = useState({
    title: initial?.title || "", description: initial?.description || "",
    price: initial?.price != null ? String(initial.price) : "", price_unit: initial?.price_unit || "",
    subcategory: initial?.subcategory || "", country: initial?.country || "", city: initial?.city || "",
    address: initial?.address || "", postal_code: initial?.postal_code || "", phone: initial?.phone || "", whatsapp: initial?.whatsapp || "",
    images: (initial?.images || []).join(", "),
  });
  const [loading, setLoading] = useState(false);
  const [pa, setPa] = useState(!!initial?.proximity_alert);
  const [sa, setSa] = useState(initial?.social_allowed ?? null);
  const [am, setAm] = useState(initial?.allow_messages ?? true);
  const [picked, setPicked] = useState(null);
  const [ruleOk, setRuleOk] = useState(false);
  const cat = category;

  const submit = async () => {
    if (sa === null) { toast.error(SOCIAL_MSG); return; }
    if (!ruleOk) { toast.error(RULES_MSG); return; }
    if (!f.title) return toast.error("Shkruaj një titull");
    setLoading(true);
    const coords = picked || (initial?.lat != null ? { lat: initial.lat, lng: initial.lng } : null) || cityCoords(f.country, f.city);
    try {
      const payload = {
        category: cat.key, subcategory: f.subcategory, title: f.title, description: f.description,
        price: f.price ? parseFloat(f.price) : null, price_unit: f.price_unit,
        country: f.country, city: f.city, address: f.address, postal_code: f.postal_code || "",
        lat: coords ? coords.lat : null, lng: coords ? coords.lng : null,
        images: f.images ? f.images.split(",").map((s) => s.trim()).filter(Boolean) : [],
        phone: f.phone, whatsapp: f.whatsapp, proximity_alert: pa, social_allowed: sa, allow_messages: am, data: initial?.data || {},
      };
      const r = initial ? await api.put(`/listings/${initial.id}`, payload) : await api.post("/listings", payload);
      toast.success(initial ? "U dërgua për aprovim" : "Shpallja u publikua!");
      const id = initial ? initial.id : r.data.id;
      onDone ? onDone(id) : nav(`/shpallje/${id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className="gs-fade px-4 pt-4 pb-10">
      <button data-testid="generic-back" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />{initial ? "Anulo" : "Publiko"}</button>
      <h1 className="text-xl font-extrabold text-slate-900">{initial ? "Ndrysho" : "Publiko"}: {cat.label}</h1>
      <div className="space-y-3 mt-4">
        <Input data-testid="publish-title" placeholder="Titulli *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl h-11" />
        {SUBCATEGORIES[cat.key] && (
          <select data-testid="publish-subcategory" value={f.subcategory} onChange={(e) => setF({ ...f, subcategory: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none">
            <option value="">Nënkategoria</option>
            {SUBCATEGORIES[cat.key].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <Textarea data-testid="publish-description" placeholder="Përshkrimi" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" />
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="publish-price" type="number" placeholder="Çmimi €" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="rounded-xl h-11" />
          <Input data-testid="publish-price-unit" placeholder="/muaj, /orë..." value={f.price_unit} onChange={(e) => setF({ ...f, price_unit: e.target.value })} className="rounded-xl h-11" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="publish-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, city: "" })} className="w-full bg-white border border-slate-200 rounded-xl h-11 px-2 text-sm outline-none">
            <option value="">Shteti</option>
            {nearCountries().map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
          </select>
          <CityInput setCoords={setPicked} testid="publish-city" country={f.country} value={f.city} onChange={(v) => setF((x) => ({ ...x, city: v }))} cities={Object.keys(CITIES_BY_COUNTRY[f.country] || {})} className="w-full bg-white border border-slate-200 rounded-xl h-11 px-2 text-sm outline-none disabled:bg-slate-50 disabled:text-slate-400" />
        </div>
        <AddressInput testid="publish-address" placeholder="Adresa / zona" f={f} setF={setF} setCoords={setPicked} className="w-full rounded-xl h-11 border border-slate-200 px-3 text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="publish-phone" placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-xl h-11" />
          <Input data-testid="publish-whatsapp" placeholder="WhatsApp" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} className="rounded-xl h-11" />
        </div>
        <Input data-testid="publish-images" placeholder="URL foto (ndaj me presje)" value={f.images} onChange={(e) => setF({ ...f, images: e.target.value })} className="rounded-xl h-11" />
        <ProximityToggle value={pa} onChange={setPa} />
        <SocialConsent value={sa} onChange={setSa} />
        <MessagesConsent value={am} onChange={setAm} />
        <RulesBox catKey={category.key} accepted={ruleOk} onAccept={setRuleOk} />
        <Button data-testid="publish-submit" onClick={submit} disabled={loading} className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 font-bold text-base">{loading ? "Duke ruajtur..." : initial ? "Ruaj ndryshimet" : "Publiko"}</Button>
      </div>
    </div>
  );
}

export default GenericForm;
